import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { TableRowEditorField } from "./table-row-editor-fields";

type TableRowEditorMode = "create" | "edit";

interface TableRowDataEditorProps {
  open: boolean;
  mode: TableRowEditorMode;
  fields: TableRowEditorField[];
  rowData: Record<string, unknown>;
  embedded?: boolean;
  mobileViewport?: boolean;
  readOnly?: boolean;
  showHeader?: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (rowData: Record<string, unknown>) => void | Promise<unknown>;
  onDelete?: () => void | Promise<unknown>;
}

const getInputValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const coerceFieldValue = (
  field: TableRowEditorField,
  value: string | boolean,
): unknown => {
  if (field.inputType === "checkbox") return value === true;
  if (field.inputType === "number") {
    if (typeof value !== "string" || value.trim().length === 0) return "";
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : value;
  }
  return typeof value === "string" ? value : "";
};

export const TableRowDataEditor: React.FC<TableRowDataEditorProps> = ({
  open,
  mode,
  fields,
  rowData,
  embedded = false,
  mobileViewport = false,
  readOnly = false,
  showHeader = true,
  onOpenChange,
  onSave,
  onDelete,
}) => {
  const formRef = React.useRef<HTMLFormElement>(null);
  const [isEditing, setIsEditing] = React.useState(mode === "create");
  const stopEditorClickPropagation = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
    },
    [],
  );

  React.useEffect(() => {
    setIsEditing(mode === "create");
  }, [mode, open, rowData]);

  const effectiveReadOnly = readOnly || !isEditing;

  const handleSave = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (effectiveReadOnly) return;

      const form = formRef.current;
      if (!form) return;

      const formData = new FormData(form);
      const nextRow: Record<string, unknown> = { ...rowData };
      fields.forEach((field) => {
        if (field.inputType === "checkbox") {
          const control = form.elements.namedItem(field.key);
          nextRow[field.key] = coerceFieldValue(
            field,
            control instanceof HTMLInputElement && control.checked,
          );
          return;
        }

        const rawValue = formData.get(field.key);
        nextRow[field.key] = coerceFieldValue(
          field,
          typeof rawValue === "string" ? rawValue : "",
        );
      });
      await onSave(nextRow);
    },
    [effectiveReadOnly, fields, onSave, rowData],
  );

  const title =
    mode === "create" ? "Add Row" : effectiveReadOnly ? "View Row" : "Edit Row";
  const description =
    mode === "create"
      ? "Create a table record."
      : effectiveReadOnly
        ? "Review this table record."
        : "Edit this table record.";
  const content = (
    <>
      {showHeader ? (
        mobileViewport ? (
          <div className="shrink-0 border-b border-border/70 bg-card px-4 py-4">
            <h2 className="text-lg font-semibold leading-none text-foreground">
              {title}
            </h2>
          </div>
        ) : (
          <DialogHeader className="shrink-0 border-b border-border/70 bg-card px-6 py-5 pr-14">
            <DialogTitle className="text-2xl font-semibold tracking-tight text-foreground">
              {title}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {description}
            </DialogDescription>
          </DialogHeader>
        )
      ) : null}
      <form
        ref={formRef}
        onSubmit={handleSave}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div
          data-testid="table-row-editor-fields"
          className={`min-h-0 flex-1 overflow-y-auto ${
            mobileViewport ? "px-4 py-4" : "max-h-[calc(88vh-12rem)] px-6 py-5"
          }`}
        >
          <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2">
            {fields.map((field) => {
              const value = rowData[field.key];
              const fieldId = `table-row-field-${field.key}`;
              const isWideField =
                field.inputType === "textarea" ||
                field.key.toLowerCase().includes("description") ||
                field.key.toLowerCase().includes("notes");
              return (
                <label
                  key={field.key}
                  htmlFor={fieldId}
                  className={`grid gap-2 text-sm ${isWideField ? "sm:col-span-2" : ""}`}
                >
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {field.label}
                    {field.required ? " *" : ""}
                  </span>
                  {field.inputType === "select" ? (
                    <select
                      id={fieldId}
                      name={field.key}
                      defaultValue={getInputValue(value)}
                      disabled={effectiveReadOnly}
                      className="h-10 rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/25"
                    >
                      <option value="">Select...</option>
                      {field.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : field.inputType === "textarea" ? (
                    <textarea
                      id={fieldId}
                      name={field.key}
                      defaultValue={getInputValue(value)}
                      disabled={effectiveReadOnly}
                      className="min-h-28 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/25"
                    />
                  ) : field.inputType === "checkbox" ? (
                    <span className="flex h-10 items-center rounded-md border border-input bg-background px-3 shadow-sm">
                      <input
                        id={fieldId}
                        name={field.key}
                        type="checkbox"
                        defaultChecked={value === true}
                        disabled={effectiveReadOnly}
                        className="h-4 w-4 rounded border-input"
                      />
                    </span>
                  ) : (
                    <Input
                      id={fieldId}
                      name={field.key}
                      type={field.inputType}
                      defaultValue={getInputValue(value)}
                      disabled={effectiveReadOnly}
                      className="h-10 shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/25"
                    />
                  )}
                </label>
              );
            })}
          </div>
        </div>
        <DialogFooter
          className={`shrink-0 ${mobileViewport ? "px-4 py-4" : "px-6 py-4"}`}
        >
          {mode === "edit" && isEditing && onDelete ? (
            <Button
              type="button"
              variant="destructive"
              onClick={onDelete}
              className="sm:mr-auto"
            >
              Delete Row
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {mode === "edit" && !isEditing ? "Close" : "Cancel"}
          </Button>
          {mode === "edit" && !readOnly && !isEditing ? (
            <Button type="button" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          ) : null}
          {effectiveReadOnly ? null : <Button type="submit">Save Row</Button>}
        </DialogFooter>
      </form>
    </>
  );

  if (embedded) {
    return open ? (
      <div
        className="flex min-h-0 flex-1 flex-col bg-background text-foreground"
        onClick={stopEditorClickPropagation}
      >
        {content}
      </div>
    ) : null;
  }

  if (mobileViewport) {
    return open ? (
      <div
        className="fixed inset-0 z-[10001] flex flex-col bg-background"
        onClick={stopEditorClickPropagation}
      >
        {content}
      </div>
    ) : null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        variant="light"
        className="flex max-h-[88vh] w-[calc(100vw-2rem)] max-w-4xl flex-col p-0"
        onClick={stopEditorClickPropagation}
      >
        {content}
      </DialogContent>
    </Dialog>
  );
};
